import type { AttackDefinition, GameContent } from '../data/types';
import type { DeviceId, InputSnapshot } from '../input/intents';
import type { CommandSource } from '../net/types';
import type { CameraFocus } from '../render/types';
import { length, normalize } from '../shared/vec';
import { IDLE_INPUT } from '../sim/commands';
import { setOf } from '../sim/lineup';
import type { PlayerSlot } from '../sim/initial-state';
import type { PlayerId, PlayerState } from '../sim/state';
import { attackChargeTicks, attackReach } from '../sim/attacks';
import type { LocalPlayer } from '../ui';
import { type Assist, Controls, type ScreenToWorld } from './controls';
import { createSession, type Session } from './session';

// A local player without a device reads the merged view: a solo game, or a guest online.
export interface InputView {
  readonly devices: ReadonlyMap<DeviceId, InputSnapshot>;
  readonly merged: InputSnapshot;
}

export interface MatchOptions {
  readonly seed: number;
  readonly setId: string;
  readonly endless: boolean;
  // Absent means one stage.
  readonly festival?: boolean;
  readonly content: GameContent;
  readonly slots: readonly PlayerSlot[];
  // The players of this screen; the others come from the source.
  readonly locals: ReadonlyMap<PlayerId, DeviceId | null>;
  readonly source: CommandSource;
  readonly focus: CameraFocus;
  readonly assist?: () => Assist;
}

export interface MatchSeat {
  readonly playerId: PlayerId;
  readonly name: string | null;
  readonly classId: string;
  // `null`: the merged view, or a player played from afar.
  readonly device: DeviceId | null;
  readonly local: boolean;
}

export interface Match {
  readonly session: Session;
  readonly source: CommandSource;
  readonly focus: CameraFocus;
  readonly seats: readonly MatchSeat[];
  readonly players: readonly LocalPlayer[];
  // Once per rendered frame, before its steps.
  frame(view: InputView): void;
  chooseUpgrade(playerId: PlayerId, upgradeId: string): void;
  // False when the source has nothing yet and the sim must wait.
  step(toWorld: ScreenToWorld): boolean;
  close(): void;
}

const IDLE_GAMEPLAY = {
  ...IDLE_INPUT,
  placeTrap: false,
  dash: false,
  emote: null,
  nextTrap: false,
  previousTrap: false,
  selectTrap: null,
  pause: false,
} as const;

const NO_MENU = {
  up: false,
  down: false,
  left: false,
  right: false,
  previous: false,
  next: false,
  confirm: false,
  back: false,
} as const;

export const IDLE_SNAPSHOT: InputSnapshot = {
  device: 'none',
  gameplay: IDLE_GAMEPLAY,
  menu: NO_MENU,
  pointerScreen: null,
  aimFromPointer: false,
};

// Any press that pauses, resumes or falls in the pause acts on nothing else: the A that resumes must not place a trap.
export function withoutPresses(snapshot: InputSnapshot): InputSnapshot {
  return {
    ...snapshot,
    gameplay: {
      ...snapshot.gameplay,
      fire: false,
      skill: false,
      placeTrap: false,
      dash: false,
      emote: null,
      nextTrap: false,
      previousTrap: false,
      selectTrap: null,
      pause: false,
    },
    menu: NO_MENU,
  };
}

export function withoutPressesView(view: InputView): InputView {
  return {
    merged: withoutPresses(view.merged),
    devices: new Map(
      [...view.devices].map(([device, snapshot]) => [device, withoutPresses(snapshot)]),
    ),
  };
}

export function createMatch(options: MatchOptions): Match {
  const { content, slots, locals, source } = options;
  const session = createSession({
    seed: options.seed,
    players: slots,
    setId: options.setId,
    content,
    endless: options.endless,
    ...(options.festival === true ? { festival: true } : {}),
  });
  const controls = new Map<PlayerId, Controls>();
  const { handSize } = setOf(content, options.setId);
  for (const playerId of locals.keys()) {
    controls.set(playerId, new Controls(handSize, outwardAim(session, playerId)));
  }
  const seats: MatchSeat[] = slots.map((slot) => ({
    playerId: slot.id,
    name: slot.name ?? null,
    classId: slot.classId,
    device: locals.get(slot.id) ?? null,
    local: locals.has(slot.id),
  }));
  let players: LocalPlayer[] = [];

  return {
    session,
    source,
    focus: options.focus,
    seats,
    get players() {
      return players;
    },
    frame(view) {
      players = [];
      for (const [playerId, device] of locals) {
        // A controller unplugged mid-game reads as idle: the player waits for it to come back.
        const snapshot =
          device === null ? view.merged : (view.devices.get(device) ?? IDLE_SNAPSHOT);
        controls.get(playerId)?.frame(snapshot);
        players.push({ playerId, snapshot });
      }
    },
    chooseUpgrade(playerId, upgradeId) {
      controls.get(playerId)?.chooseUpgrade(upgradeId);
    },
    step(toWorld) {
      const commands = source.next(
        [...controls].map(([playerId, own]) => {
          const player = session.state.players.find((candidate) => candidate.id === playerId);
          if (player === undefined) {
            throw new Error(`The game has no player ${String(playerId)}`);
          }
          const attack = attackOf(content, player);
          return own.command(
            player,
            toWorld,
            session.state.enemies,
            attackReach(attack, player),
            options.assist?.(),
            attackChargeTicks(attack),
          );
        }),
      );
      if (commands === null) {
        return false;
      }
      session.step(commands);
      source.stepped(session.state);
      return true;
    },
    close() {
      source.close();
    },
  };
}

// Away from the scene, where the bad vibes come from and where a trap placed in front of the player fits.
function outwardAim({ state }: Session, playerId: PlayerId) {
  const player = state.players.find((candidate) => candidate.id === playerId);
  const outward =
    player === undefined
      ? null
      : normalize({ x: player.x - state.core.x, y: player.y - state.core.y });
  return outward !== null && length(outward) > 0 ? outward : IDLE_INPUT.aim;
}

function attackOf(content: GameContent, player: PlayerState): AttackDefinition {
  const definition = content.classes.find((candidate) => candidate.id === player.classId);
  if (definition === undefined) {
    throw new Error(`The game has no class ${player.classId}`);
  }
  return definition.attack;
}
